import 'raf/polyfill' // https://docs.swmansion.com/react-native-reanimated/docs/fundamentals/web-support/#solito--nextjs-compatibility
import '@/styles/globals.css'
import React, { useEffect } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { NextPage } from 'next';
import Head from 'next/head'
import type { AppProps } from 'next/app'
import { useRouter } from 'next/router';
import TrackPlayer from 'react-native-track-player';

import { TrackPlayerService } from '@annunciate/player/dist/services';
import {
  SegmentAnalyticsService,
  DEFAULT_TOPICS,
  SEGMENT_VIDEO_TOPICS,
} from '@annunciate/segment-analytics';
import {
  GoogleAnalyticsEnrichmentPlugin,
  MetaPixelEnrichmentPlugin,
} from '@annunciate/segment-analytics/dist/plugins';
import Config from '@annunciate/core/dist/utils/config';
import { Topic } from '@annunciate/core/dist/events';

import { client } from '../queryClient';
import { Layout } from '@/components/Layout';
import { version as appVersion } from '@/../package.json';
import { useNextPageviewListener } from '@/hooks';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export default function App({
  Component,
  pageProps: { dehydratedState, ...pageProps },
}: AppPropsWithLayout) {
  const router = useRouter();
  useNextPageviewListener(router);

  useEffect(() => {
    TrackPlayer.registerPlaybackService(() => {
      // initialize jitsu wrapper
      SegmentAnalyticsService({
        appVersion,
        appName: 'Annunciate Dev Client',
        proxy: `${Config.JITSU_HOST}/v1/batch`,
        cdnProxy: `${Config.JITSU_HOST}/v1/projects`,
        writeKey: Config.JITSU_WRITE_KEY || '',
        topics: [
          ...DEFAULT_TOPICS,
          ...SEGMENT_VIDEO_TOPICS,
        ],
        additionalPlugins: [
          new GoogleAnalyticsEnrichmentPlugin(),
          new MetaPixelEnrichmentPlugin(),
        ],
      });

      return () => TrackPlayerService(client);
    });
  }, []);

  const getLayout = Component.getLayout || ((page) => (
    <Layout dehydratedState={dehydratedState}>
      {page}
    </Layout>
  ));

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* NOTE: this fixes an issue in react-native-reanimated */}
        <script>if (!window.global) window.global = globalThis;</script>
      </Head>
      {getLayout(<Component {...pageProps} />)}
    </>
  );
}
